import { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { css } from '@emotion/react';
import ReactRouterLink from '../../components/ReactRouterLink';
import PageContainer from '../../components/PageContainer';
import Button from '../../components/Button';
import ResponseStatus from '../../constants/ResponseStatus';
import { getFirstError, navigateToErrorPage } from '../../services/errorUtils';
import useMessageService from '../../services/i18n/useMessageService';
import { register } from '../../veevaIdApi';
import UsernameMask from '../../components/UsernameMask';
import PasswordField from '../../components/PasswordField';
import hasValidASCIICharacters from '../../services/hasValidASCIICharacters';
import useNetworkErrorNotification from '../../services/useNetworkErrorNotification';
import LoadingScreenContext from '../../services/LoadingScreenContext';
import { AuthenticatedPaths, UnauthenticatedPaths } from '../../constants/Paths';
import getTargetAttribute from '../../services/getTargetAttribute';

const RegisterActiveUser = ({ firstName, lastName, username }) => {
    const history = useHistory();
    const { setLoading } = useContext(LoadingScreenContext);
    const [password, setPassword] = useState('');
    const [passwordFieldError, setPasswordFieldError] = useState('');
    const { showNotification } = useNetworkErrorNotification();

    const [
        [
            welcomeBackLabel,
            loginButtonLabel,
            enterPasswordPlaceholderLabel,
            invalidPasswordLabel,
            forgotPasswordLabel,
        ],
    ] = useMessageService([
        'general.welcome_back',
        'general.log_in',
        'password.enter_password_placeholder',
        'login.invalid_password',
        'forgot_password.having_trouble_login',
    ]);

    const handleChangePassword = (value) => {
        setPassword(value);
        setPasswordFieldError(undefined);
    };

    const handleRegistrationResponse = (registrationResponse) => {
        const { responseStatus } = registrationResponse;

        switch (responseStatus) {
            case ResponseStatus.SUCCESS:
                history.replace(AuthenticatedPaths.REGISTRATION_SUCCESS);
                break;
            case ResponseStatus.FAILURE: {
                const error = getFirstError(registrationResponse);
                navigateToErrorPage(history, error);
                break;
            }
            case ResponseStatus.UI_NETWORK_ERROR:
                showNotification();
                break;
            default:
            // No-op
        }
    };

    const handleSubmit = async () => {
        const validPassword = hasValidASCIICharacters(password);

        if (validPassword) {
            setLoading(true);
            const saveRegisterResult = await register(firstName, lastName, password);
            setLoading(false);
            handleRegistrationResponse(saveRegisterResult);
        } else {
            setPasswordFieldError(invalidPasswordLabel);
        }
    };

    return (
        <PageContainer title={welcomeBackLabel}>
            <UsernameMask
                firstName={firstName}
                lastName={lastName}
                username={username}
                showSwitchUser={false}
            />
            <PasswordField
                errorMessage={passwordFieldError}
                onChange={handleChangePassword}
                onKeyUp={handleSubmit}
                placeholder={enterPasswordPlaceholderLabel}
                value={password}
            />
            <Button
                type="submit"
                onClick={handleSubmit}
                disabled={!password}
                data-target={getTargetAttribute('submit-button')}
            >
                {loginButtonLabel}
            </Button>
            <ReactRouterLink
                css={({ linkFontSize }) => {
                    return css`
                        display: inline-block;
                        margin-top: 1rem;
                        font-size: ${linkFontSize};
                    `;
                }}
                to={UnauthenticatedPaths.FORGOT_PASSWORD}
            >
                {forgotPasswordLabel}
            </ReactRouterLink>
        </PageContainer>
    );
};

RegisterActiveUser.propTypes = {
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    username: PropTypes.string.isRequired,
};

export default RegisterActiveUser;
